//CameraSection.tsx — camera, rotation and mirror rows, shared by the try-on and avatar settings

import { useEffect, useState } from 'react'
import { listCameras, type CameraDevice } from '../camera/devices'
import type { QuarterTurns, VideoPreference } from '../config/videoPreference'
import { Row, Section } from './panel'

const TURNS: QuarterTurns[] = [0, 1, 2, 3]

interface Props {
	preference: VideoPreference
	onChange: (patch: Partial<VideoPreference>) => void
}

export function CameraSection({ preference, onChange }: Props) {
	const [cameras, setCameras] = useState<CameraDevice[]>([])

	useEffect(() => {
		//Device labels are blank until a camera has been granted once, so this runs after the
		//preview is already up.
		void listCameras().then(setCameras)
	}, [])

	return (
		<Section title="Camera">
			<Row label="Camera">
				<select
					value={preference.deviceId ?? ''}
					onChange={(event) => onChange({ deviceId: event.target.value || null })}
				>
					<option value="">Automatic</option>
					{cameras.map((camera) => (
						<option key={camera.deviceId} value={camera.deviceId}>
							{camera.label}
						</option>
					))}
				</select>
			</Row>

			<Row label="Rotate camera" hint="For a physically tilted camera.">
				<div className="segmented" role="group">
					{TURNS.map((turns) => (
						<button
							key={turns}
							type="button"
							className={preference.captureRotation === turns ? 'is-active' : ''}
							onClick={() => onChange({ captureRotation: turns })}
							aria-pressed={preference.captureRotation === turns}
						>
							{turns * 90}°
						</button>
					))}
				</div>
			</Row>

			<Row label="Mirror">
				<input
					type="checkbox"
					checked={preference.mirror}
					onChange={(event) => onChange({ mirror: event.target.checked })}
				/>
			</Row>
		</Section>
	)
}
